import { useMemo, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import {
  IMPORT_FIELDS,
  IMPORT_TEMPLATE_HEADERS,
  decodeText,
  detectColumns,
  parseCsv,
  parseImportRows,
  planImport,
  uuidv7,
  type ColumnMapping,
  type ImportField,
} from '@pos/core';
import { applyLocalChanges } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, PageHeader, Select, Spinner, Toggle, errorMessage } from '../../ui/index.tsx';
import { buildImportChanges, describeImportUpdates, type ImportTarget } from './changes.ts';
import { useCatalog, useMoney, type Catalog } from './data.ts';

const MAX_BYTES = 15 * 1024 * 1024;
const MAX_ROWS = 20_000;
const CHUNK = 1000;

type Sheet = { fileName: string; header: unknown[]; rows: unknown[][] };

/** Lee la primera hoja de un Excel/ODS (con SheetJS, que se descarga recién acá) o un CSV. */
async function readSheet(file: File): Promise<Sheet> {
  if (file.size > MAX_BYTES) throw new Error('El archivo es demasiado grande (máximo 15 MB).');
  let all: unknown[][];
  if (/\.(csv|txt)$/i.test(file.name)) {
    all = parseCsv(decodeText(new Uint8Array(await file.arrayBuffer())));
  } else {
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]!];
    if (!sheet) throw new Error('El archivo no tiene hojas.');
    all = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, defval: '' });
  }
  // El encabezado es la primera fila con al menos dos celdas con texto.
  const headerIndex = all.findIndex((r) => r.filter((c) => String(c ?? '').trim() !== '').length >= 2);
  if (headerIndex < 0) throw new Error('No encontramos datos en el archivo.');
  const rows = all.slice(headerIndex + 1);
  if (rows.length > MAX_ROWS) throw new Error(`El archivo tiene más de ${MAX_ROWS.toLocaleString('es-AR')} filas. Dividilo en partes.`);
  return { fileName: file.name, header: all[headerIndex]!, rows };
}

async function downloadTemplate() {
  const XLSX = await import('xlsx');
  const sheet = XLSX.utils.aoa_to_sheet([
    IMPORT_TEMPLATE_HEADERS,
    ['Coca-Cola 500 ml', '7790895000997', 'CC500', 1500, 950, 'Bebidas', 'Distribuidora Sur', 'u', 24, 6, 21],
    ['Pan francés', '', 'PAN', 2800, 1900, 'Panificados', '', 'kg', 5.5, 2, 10.5],
  ]);
  // Columna de códigos como texto: si no, Excel los muestra como 7,79E+12.
  sheet['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 18 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 6 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Productos');
  XLSX.writeFile(workbook, 'planilla-productos.xlsx');
}

export function ImportPage() {
  const catalog = useCatalog();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setReading(true);
    setError(null);
    try {
      const read = await readSheet(file);
      setSheet(read);
      setMapping(detectColumns(read.header));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setReading(false);
    }
  };

  if (!catalog) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <PageHeader
        title="Importar productos"
        description="Desde Excel (.xlsx, .xls), LibreOffice (.ods) o CSV."
        actions={
          <Link to="/productos">
            <Button variant="secondary">Volver</Button>
          </Link>
        }
      />

      {!sheet ? (
        <Card className="space-y-4">
          <p className="text-sm text-slate-600">
            La planilla necesita una fila de títulos y al menos el <strong>nombre</strong> y el <strong>precio</strong> de cada producto. También
            puede tener código de barras, código interno, costo, categoría, proveedor, unidad (u, kg, l, m), stock mínimo e IVA. Los títulos
            se reconocen solos; después podés corregirlos.
          </p>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex cursor-pointer">
              <input type="file" accept=".xlsx,.xls,.ods,.csv,.txt" className="sr-only" onChange={onFile} disabled={reading} />
              <span className="inline-flex h-12 items-center gap-2 rounded-lg bg-brand px-5 font-medium text-brand-fg">
                {reading && <Spinner className="size-4" />}
                Elegir archivo
              </span>
            </label>
            <Button variant="secondary" size="lg" onClick={() => void downloadTemplate()}>
              Descargar planilla de ejemplo
            </Button>
          </div>
          {error && <Alert tone="error">{error}</Alert>}
        </Card>
      ) : (
        <ImportReview
          sheet={sheet}
          mapping={mapping}
          setMapping={setMapping}
          catalog={catalog}
          onReset={() => {
            setSheet(null);
            setMapping({});
          }}
        />
      )}
    </div>
  );
}

function ImportReview({
  sheet,
  mapping,
  setMapping,
  catalog,
  onReset,
}: {
  sheet: Sheet;
  mapping: ColumnMapping;
  setMapping: (m: ColumnMapping) => void;
  catalog: Catalog;
  onReset: () => void;
}) {
  const { business, writer, device, can } = useSession();
  const withStock = can('adjust_stock');
  const money = useMoney();
  const [updateExisting, setUpdateExisting] = useState(true);
  const [progress, setProgress] = useState<number | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const missing = (Object.keys(IMPORT_FIELDS) as ImportField[]).filter((f) => IMPORT_FIELDS[f].required && mapping[f] === undefined);

  const analysis = useMemo(() => {
    if (missing.length) return null;
    const parsed = parseImportRows(sheet.rows, mapping);
    const byBarcode = new Map<string, ImportTarget>();
    const bySku = new Map<string, ImportTarget>();
    for (const v of catalog.variants) {
      const target = { variant: v, cost: catalog.costs.get(v.id) };
      if (v.barcode) byBarcode.set(v.barcode, target);
      if (v.sku) bySku.set(v.sku, target);
    }
    const plan = planImport(parsed.rows, { byBarcode, bySku });
    const result = buildImportChanges(plan, {
      businessId: business.id,
      now: new Date().toISOString(),
      newId: uuidv7,
      categories: catalog.categories,
      suppliers: catalog.suppliers,
      updateExisting,
      initialStock: withStock ? { deviceId: device.id } : undefined,
    });
    const updates = describeImportUpdates(plan, (id) => catalog.byId.get(id)?.product.name ?? '(producto)');
    return { parsed, plan, result, updates };
  }, [sheet, mapping, catalog, updateExisting, business.id, missing.length, withStock, device.id]);

  const columns = sheet.header.map((h, i) => ({ index: i, label: String(h ?? '').trim() || `Columna ${i + 1}` }));

  const apply = async () => {
    if (!analysis) return;
    const { changes } = analysis.result;
    setError(null);
    setProgress(0);
    try {
      for (let i = 0; i < changes.length; i += CHUNK) {
        await applyLocalChanges(db, changes.slice(i, i + CHUNK), writer);
        setProgress(Math.min(changes.length, i + CHUNK) / changes.length);
      }
      requestSync(0);
      const { created, updated } = analysis.result;
      setDone(
        `Listo: ${created} producto${created === 1 ? '' : 's'} nuevo${created === 1 ? '' : 's'}` +
          (updated ? ` y ${updated} actualizado${updated === 1 ? '' : 's'}` : '') +
          '. Se están enviando al servidor en segundo plano.',
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setProgress(null);
    }
  };

  if (done) {
    return (
      <Card className="space-y-4">
        <Alert tone="success">{done}</Alert>
        <div className="flex gap-2">
          <Link to="/productos">
            <Button>Ver productos</Button>
          </Link>
          <Button variant="secondary" onClick={onReset}>
            Importar otro archivo
          </Button>
        </div>
      </Card>
    );
  }

  const errorsCsv = () => {
    if (!analysis) return;
    const lines = ['Fila;Problema', ...analysis.parsed.errors.map((e) => `${e.row};"${e.message.replace(/"/g, '""')}"`)];
    const url = URL.createObjectURL(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'errores-importacion.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Card className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">1. Columnas de “{sheet.fileName}”</h2>
          <Button variant="ghost" onClick={onReset}>
            Elegir otro archivo
          </Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(Object.keys(IMPORT_FIELDS) as ImportField[]).map((field) => (
            <label key={field} className="block space-y-1">
              <span className="text-sm font-medium text-slate-700">
                {IMPORT_FIELDS[field].label}
                {IMPORT_FIELDS[field].required && <span className="text-red-600"> *</span>}
              </span>
              <Select
                value={mapping[field] ?? ''}
                onChange={(e) => {
                  const next = { ...mapping };
                  if (e.target.value === '') delete next[field];
                  else next[field] = Number(e.target.value);
                  setMapping(next);
                }}
              >
                <option value="">— No importar —</option>
                {columns.map((c) => (
                  <option key={c.index} value={c.index}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </label>
          ))}
        </div>
        {missing.length > 0 && (
          <Alert tone="warning">Indicá qué columna tiene: {missing.map((f) => IMPORT_FIELDS[f].label.toLowerCase()).join(' y ')}.</Alert>
        )}
        <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                {columns.map((c) => (
                  <th key={c.index} className="px-3 py-2 font-medium whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sheet.rows.slice(0, 4).map((r, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.index} className="max-w-48 truncate px-3 py-1.5 whitespace-nowrap">
                      {String(r[c.index] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {analysis && (
        <Card className="space-y-4">
          <h2 className="font-semibold">2. Revisá</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Productos nuevos" value={analysis.result.created} />
            <Stat label="Ya existen (por código)" value={analysis.plan.updates.length} />
            <Stat label="Filas con errores" value={analysis.parsed.errors.length} tone={analysis.parsed.errors.length ? 'red' : undefined} />
          </div>
          <Toggle
            checked={updateExisting}
            onChange={setUpdateExisting}
            label="Actualizar precio, costo y stock mínimo de los que ya existen"
            description="Se reconocen por código de barras o código interno. El nombre y la categoría no se cambian."
          />
          {analysis.updates.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">
                {updateExisting
                  ? 'Estos ya están en tu catálogo (mismo código) y se van a actualizar:'
                  : 'Estos ya están en tu catálogo (mismo código) y no se van a modificar:'}
              </p>
              {analysis.updates.some((u) => u.nameMismatch) && (
                <Alert tone="warning" title="Revisá los nombres marcados">
                  En la planilla tienen otro nombre que en tu catálogo. Si no son el mismo producto, el código está mal en alguno de los
                  dos: corregilo antes de importar, o se va a actualizar el producto equivocado.
                </Alert>
              )}
              <ul className="max-h-60 divide-y divide-slate-100 overflow-y-auto rounded-lg text-sm ring-1 ring-slate-200">
                {analysis.updates.slice(0, 100).map((u) => (
                  <li key={u.row} className={u.nameMismatch ? 'bg-amber-50 px-3 py-1.5' : 'px-3 py-1.5'}>
                    <strong>{u.catalogName}</strong>
                    {u.nameMismatch && <span className="text-amber-800"> · en la planilla: “{u.fileName}”</span>}
                    <span className="text-slate-500">
                      {' '}
                      · fila {u.row} · {u.matchedBy === 'barcode' ? 'cód. barras' : 'cód. interno'} {u.code}
                      {updateExisting && u.oldPrice !== u.newPrice && <> · {money(u.oldPrice)} → {money(u.newPrice)}</>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(analysis.result.newCategories.length > 0 || analysis.result.newSuppliers.length > 0) && (
            <p className="text-sm text-slate-600">
              {analysis.result.newCategories.length > 0 && <>Categorías nuevas: {analysis.result.newCategories.join(', ')}. </>}
              {analysis.result.newSuppliers.length > 0 && <>Proveedores nuevos: {analysis.result.newSuppliers.join(', ')}.</>}
            </p>
          )}
          {analysis.parsed.errors.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Estas filas no se importan (podés corregirlas en la planilla y volver a importar):</p>
              <ul className="max-h-60 divide-y divide-slate-100 overflow-y-auto rounded-lg text-sm ring-1 ring-slate-200">
                {analysis.parsed.errors.slice(0, 100).map((e) => (
                  <li key={e.row} className="px-3 py-1.5">
                    <strong>Fila {e.row}:</strong> {e.message}
                  </li>
                ))}
              </ul>
              <Button variant="ghost" onClick={errorsCsv}>
                Descargar lista de errores
              </Button>
            </div>
          )}
          {error && <Alert tone="error">{error}</Alert>}
          {progress !== null ? (
            <div className="space-y-1">
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full bg-brand transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
              <p className="text-sm text-slate-500">Guardando… {Math.round(progress * 100)} %</p>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button size="lg" disabled={analysis.result.changes.length === 0} onClick={apply}>
                Importar
              </Button>
            </div>
          )}
        </Card>
      )}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'red' }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="text-sm text-slate-500">{label}</p>
      <p className={tone === 'red' ? 'text-2xl font-semibold text-red-700' : 'text-2xl font-semibold'}>{value.toLocaleString('es-AR')}</p>
    </div>
  );
}
